import { CharacterData } from "../../../../../utils/game/character/CharacterData";
import { Black, White } from "../../../../../utils/sys/Color";
import { KeyValuePair } from "../../../../../utils/sys/KeyValuePair";
import { Whitelist, WhitelistUtils } from "../../../../../utils/sys/Whitelist";
import { ReactElement, useCallback, useMemo, useState } from "react";

import AlignmentLabels from "../../../../../utils/sys/AlignmentLabels";
import AlignmentMeter from "../../AlignmentMeter/AlignmentMeter";
import FlexColumn from "../../../../../common/components/layout/Flexbox/FlexColumn";
import FlexRow from "../../../../../common/components/layout/Flexbox/FlexRow";
import Meter from "../../../../../common/components/input/Meter/Meter";
import MultiLineTextField from "../../../../../common/components/input/MultiLineTextInputToggleField/MultiLineTextInputToggleField";
import NumberInputToggleField from "../../../../../common/components/input/NumberInputToggleField/NumberInputToggleField";
import TagList from "../../../../../common/components/display/TagList/TagList";
import TextField from "../../../../../common/components/display/TextField/TextField";
import TextInput from "../../../../../common/components/input/TextInput/TextInput";
import ReputationLabels from "../../../../../utils/sys/ReputationLabels";
import TabControl from "../../../../../common/components/layout/TabControl/TabControl";
import TextInputToggleField from "../../../../../common/components/input/TextInputToggleField/TextInputToggleField";
import Tooltip from "../../../../../common/components/wrappers/Tooltip/Tooltip";
import WhitelistEditorModal from "../../../../../common/components/modals/WhitelistEditorModal/WhitelistEditorModal";

import "./CharacterSheetInfoTab_style.css";

type Props = {
    characterData: CharacterData;
    isEditable?: boolean;
    selectedTab?: string | null;
    onKeywordClick?: (keyword: string) => void;
    onSelectedTabChanged?: (tab: string | null) => void;
    onCharacterDataValueChanged?: (vals: KeyValuePair[]) => void;
};

export default function CharacterSheetInfoTab({characterData, isEditable, selectedTab, onKeywordClick, onSelectedTabChanged, onCharacterDataValueChanged}: Props): ReactElement {
    const [selectedInfoTab, setSelectedInfoTab] = useState<string | null>(
        selectedTab ?? null
    );

    const [isWhitelistModalVisible, setWhitelistModalVisibility] =
        useState<boolean>(false);

    const [moduleInputText, setModuleInputText] = useState<string>("");
    const [moduleTags, setModuleTags] = useState<Array<string>>(
        characterData.modules
    );

    const conductLabel = useMemo(() => {
        return AlignmentLabels.getConductLabel(characterData.conduct);
    }, [characterData.conduct]);
    const moralityLabel = useMemo(() => {
        return AlignmentLabels.getMoralityLabel(characterData.morality);
    }, [characterData.morality]);
    const alignmentLabel = useMemo(() => {
        return AlignmentLabels.getAlignmentLabel(
            characterData.conduct,
            characterData.morality
        );
    }, [characterData.conduct, characterData.morality]);

    const fameLabel = useMemo(() => {
        return ReputationLabels.getFameLabel(characterData.fame);
    }, [characterData.fame]);
    const infamyLabel = useMemo(() => {
        return ReputationLabels.getInfamyLabel(characterData.infamy);
    }, [characterData.infamy]);
    const reputationLabel = useMemo(() => {
        return ReputationLabels.getReputationLabel(
            characterData.fame,
            characterData.infamy
        );
    }, [characterData.fame, characterData.infamy]);

    const addModule = useCallback(
        (module: string) => {
            if (!moduleTags.includes(module)) {
                const newModuleTags = [...moduleTags, module];
                setModuleTags(newModuleTags);
                setModuleInputText("");

                onCharacterDataValueChanged?.([
                    {
                        key: "modules",
                        value: newModuleTags.join("||"),
                    },
                ]);
            }
        },
        [
            moduleTags,
            setModuleTags,
            setModuleInputText,
            onCharacterDataValueChanged,
        ]
    );

    const removeModule = useCallback(
        (module: string) => {
            if (moduleTags.includes(module)) {
                const newModuleTags = moduleTags.filter((m) => m !== module);
                setModuleTags(newModuleTags);
                setModuleInputText("");

                onCharacterDataValueChanged?.([
                    {
                        key: "modules",
                        value: newModuleTags.join("||"),
                    },
                ]);
            }
        },
        [
            moduleTags,
            setModuleTags,
            setModuleInputText,
            onCharacterDataValueChanged,
        ]
    );

    return (
        <FlexColumn>
            <TabControl
                id="notes_tab_control"
                selectedTab={selectedInfoTab}
                onSelectedTabChange={(tab) => {
                    setSelectedInfoTab(() => {
                        onSelectedTabChanged?.(tab);
                        return tab;
                    });
                }}
                tabs={[
                    { id: "bio", label: "Bio" },
                    { id: "appearance", label: "Appearance" },
                    { id: "alignment", label: "Alignment/Reputation" },
                    { id: "other", label: "Other" },
                ]}
            />
            {selectedInfoTab === "bio" && (
                <FlexColumn>
                    <TextInputToggleField
                        label="Name"
                        isEditable={isEditable}
                        value={characterData.name}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "name",
                                    value: val,
                                },
                            ]);
                        }}
                    />
                    <TextInputToggleField
                        label="Birth"
                        isEditable={isEditable}
                        value={characterData.birth}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "birth",
                                    value: val,
                                },
                            ]);
                        }}
                    />
                    <NumberInputToggleField
                        label="Level"
                        value={characterData.level}
                        isEditable={isEditable}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                { key: "level", value: val },
                            ]);
                        }}
                        onLabelClick={() => {
                            onKeywordClick?.("Character Level");
                        }}
                    />
                    <TextInputToggleField
                        label="Homeland"
                        isEditable={isEditable}
                        value={characterData.home}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "home",
                                    value: val,
                                },
                            ]);
                        }}
                    />
                    <MultiLineTextField
                        placeholderText="Biography"
                        isEditable={isEditable}
                        value={characterData.biography}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "biography",
                                    value: val,
                                },
                            ]);
                        }}
                        numberOfLines={20}
                    />
                </FlexColumn>
            )}
            {selectedInfoTab === "appearance" && (
                <FlexColumn>
                    <TextInputToggleField
                        label="Race"
                        isEditable={isEditable}
                        value={characterData.raceText}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "race",
                                    value: val,
                                },
                            ]); 
                        }}
                        onLabelClick={() => {
                            onKeywordClick?.("Race");
                        }}
                    />
                    <TextInputToggleField
                        label="Sex"
                        isEditable={isEditable}
                        value={characterData.gender}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "gender",
                                    value: val,
                                },
                            ]);
                        }}
                    />
                    <TextInputToggleField
                        label="Age"
                        isEditable={isEditable}
                        value={characterData.age}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "age",
                                    value: val,
                                },
                            ]);
                        }}
                    />
                    <TextInputToggleField
                        label="Height"
                        isEditable={isEditable}
                        value={characterData.height}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "height",
                                    value: val,
                                },
                            ]);
                        }}
                    />
                    <TextInputToggleField
                        label="Weight"
                        isEditable={isEditable}
                        value={characterData.weight}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "weight",
                                    value: val,
                                },
                            ]);
                        }}
                    />
                    <TextInputToggleField
                        label="Eyes"
                        isEditable={isEditable}
                        value={characterData.eyes}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "eyes",
                                    value: val,
                                },
                            ]);
                        }}
                    />
                    <TextInputToggleField
                        label="Hair"
                        isEditable={isEditable}
                        value={characterData.hair}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "hair",
                                    value: val,
                                },
                            ]);
                        }}
                    />
                    <MultiLineTextField
                        placeholderText="Appearance"
                        isEditable={isEditable}
                        value={characterData.appearance}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "appearance",
                                    value: val,
                                },
                            ]);
                        }}
                        numberOfLines={20}
                    />
                </FlexColumn>
            )}
            {selectedInfoTab === "alignment" && (
                <FlexColumn gap={16}>
                    <FlexColumn gap={4}>
                        <FlexRow gap={8}>
                            <TextField type="bold" style={{ width: "auto" }} onClick={() => {
                                onKeywordClick?.("Conduct")
                            }}>
                                Conduct
                            </TextField>
                            <TextField style={{ width: "auto" }}>
                                {conductLabel}
                            </TextField>
                        </FlexRow>
                        <AlignmentMeter
                            currentValue={characterData.conduct}
                            isEditable={isEditable}
                            onValueChanged={(val) => {
                                onCharacterDataValueChanged?.([
                                    { key: "conduct", value: val },
                                ]);
                            }}
                        />

                        <FlexRow gap={8}>
                            <TextField type="bold" style={{ width: "auto" }} onClick={() => {
                                onKeywordClick?.("Morality")
                            }}>
                                Morality
                            </TextField>
                            <TextField style={{ width: "auto" }}>
                                {moralityLabel}
                            </TextField>
                        </FlexRow>
                        <AlignmentMeter
                            currentValue={characterData.morality}
                            isEditable={isEditable}
                            onValueChanged={(val) => {
                                onCharacterDataValueChanged?.([
                                    { key: "morality", value: val },
                                ]);
                            }}
                        />
                        <FlexRow gap={8}>
                            <TextField type="bold" style={{ width: "auto" }} onClick={() => {
                                onKeywordClick?.("Alignment")
                            }}>
                                Alignment
                            </TextField>
                            <TextField style={{ width: "auto" }}>
                                {alignmentLabel}
                            </TextField>
                        </FlexRow>
                    </FlexColumn>
                    <FlexColumn gap={4}>
                        <FlexRow gap={8}>
                            <TextField type="bold" style={{ width: "auto" }} onClick={() => {
                                onKeywordClick?.("Fame")
                            }}>
                                Fame
                            </TextField>
                            <TextField style={{ width: "auto" }}>
                                {fameLabel}
                            </TextField>
                        </FlexRow>
                        <Meter
                            fillColor={White}
                            currentValue={characterData.fame}
                            maxValue={1000}
                            isEditable={isEditable}
                            isCurrentEditable={true}
                            onValuesChanged={(vals) => {
                                if (vals.current !== characterData.fame) {
                                    onCharacterDataValueChanged?.([
                                        { key: "fame", value: vals.current },
                                    ]);
                                }
                            }}
                        />
                        <FlexRow gap={8}>
                            <TextField type="bold" style={{ width: "auto" }} onClick={() => {
                                onKeywordClick?.("Infamy")
                            }}>
                                Infamy
                            </TextField>
                            <TextField style={{ width: "auto" }}>
                                {infamyLabel}
                            </TextField>
                        </FlexRow>
                        <Meter
                            fillColor={Black}
                            currentValue={characterData.infamy}
                            maxValue={1000}
                            isEditable={isEditable}
                            isCurrentEditable={true}
                            onValuesChanged={(vals) => {
                                if (
                                    vals.current !== characterData.infamy
                                ) {
                                    onCharacterDataValueChanged?.([
                                        { key: "infamy", value: vals.current },
                                    ]);
                                }
                            }}
                        />
                        <FlexRow gap={8}>
                            <TextField type="bold" style={{ width: "auto" }} onClick={() => {
                                onKeywordClick?.("Reputation")
                            }}>
                                Reputation
                            </TextField>
                            <TextField style={{ width: "auto" }}>
                                {reputationLabel}
                            </TextField>
                        </FlexRow>
                    </FlexColumn>
                </FlexColumn>
            )}
            {selectedInfoTab === "other" && (
                <FlexColumn gap={8}>
                    {isEditable && (
                        <button
                            onClick={() => {
                                setWhitelistModalVisibility(
                                    !isWhitelistModalVisible
                                );
                            }}
                        >
                            Whitelist
                        </button>
                    )}

                    <TextField type="bold" style={{ width: "auto" }} onClick={() => {
                                onKeywordClick?.("Module")
                            }}>
                        Modules
                    </TextField>
                    {isEditable && (
                        <FlexRow gap={8}>
                            <TextInput
                                value={moduleInputText}
                                onChange={setModuleInputText}
                                onEnterKeyPress={() => {
                                    if (moduleInputText.length > 0) {
                                        addModule(moduleInputText);
                                    }
                                }}
                            />
                            <button
                                onClick={() => {
                                    if (moduleInputText.length > 0) {
                                        addModule(moduleInputText);
                                    }
                                }}
                            >
                                +
                            </button>
                        </FlexRow>
                    )}
                    <FlexRow gap={4} wrap={true}>
                        <TagList
                            tagEntries={moduleTags.map((module) => {
                                return {
                                    id: module,
                                    value: module,
                                    isEditable: isEditable,
                                };
                            })}
                            onTagRemoved={(tag) => {
                                removeModule(tag);
                            }}
                        />
                    </FlexRow>

                    <MultiLineTextField
                        placeholderText="Notes"
                        isEditable={isEditable}
                        value={characterData.notes[0]}
                        onStopEditing={(val) => {
                            onCharacterDataValueChanged?.([
                                {
                                    key: "notes_0",
                                    value: val,
                                },
                            ]);
                        }}
                        numberOfLines={15}
                    />
                </FlexColumn>
            )}
            <WhitelistEditorModal
                isVisible={isWhitelistModalVisible}
                whitelist={characterData.whitelist}
                whitelistActive={characterData.isWhitelistProtected}
                onClose={() => {
                    setWhitelistModalVisibility(false);
                }}
                onConfirm={(whitelistActive, whitelist) => {
                    onCharacterDataValueChanged?.([
                        {
                            key: "whitelist_protected",
                            value: whitelistActive ? 1 : 0,
                        },
                        {
                            key: "whitelist",
                            value: WhitelistUtils.toCSVString(whitelist),
                        },
                    ]);
                    setWhitelistModalVisibility(false);
                }}
            />
        </FlexColumn>
    );
}
